import React from 'react';
import { 
  Truck, 
  Calendar, 
  Shield, 
  Wrench, 
  Edit, 
  Trash2, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  X,
  Weight,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const VehicleDetailsDialog = ({ 
  isOpen, 
  onClose, 
  vehicle, 
  onEdit, 
  onDelete 
}) => {
  if (!vehicle) return null;

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    const colors = {
      'AVAILABLE': 'bg-green-500/20 text-green-400 border-green-500/30',
      'IN_USE': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'MAINTENANCE': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'OUT_OF_SERVICE': 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Obtenir la couleur du statut de l'assurance
  const getInsuranceColor = (status) => {
    const colors = {
      'valid': 'text-green-500',
      'expiring_soon': 'text-orange-500',
      'expired': 'text-red-500',
      'unknown': 'text-gray-500',
    };
    return colors[status] || 'text-gray-500';
  };

  // Obtenir la couleur du statut du contrôle technique
  const getTechnicalColor = (status) => {
    const colors = {
      'valid': 'text-green-500',
      'expiring_soon': 'text-orange-500',
      'expired': 'text-red-500',
      'unknown': 'text-gray-500',
    };
    return colors[status] || 'text-gray-500';
  };

  // Obtenir l'icône du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="w-4 h-4" />;
      case 'expiring_soon':
        return <Clock className="w-4 h-4" />;
      case 'expired':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Calculer l'âge de l'assurance
  const getInsuranceAge = () => {
    if (!vehicle.insurance_valid_until) return null;
    
    const validUntil = new Date(vehicle.insurance_valid_until);
    const now = new Date();
    const diffTime = validUntil - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Expirée', color: 'text-red-500' };
    } else if (diffDays <= 30) {
      return { text: `Expire dans ${diffDays} jour(s)`, color: 'text-orange-500' };
    } else {
      return { text: `Valide ${diffDays} jour(s)`, color: 'text-green-500' };
    }
  };

  // Calculer l'âge du contrôle technique
  const getTechnicalAge = () => {
    if (!vehicle.technical_control_until) return null;
    
    const validUntil = new Date(vehicle.technical_control_until);
    const now = new Date();
    const diffTime = validUntil - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Expiré', color: 'text-red-500' };
    } else if (diffDays <= 30) {
      return { text: `Expire dans ${diffDays} jour(s)`, color: 'text-orange-500' };
    } else {
      return { text: `Valide ${diffDays} jour(s)`, color: 'text-green-500' };
    }
  };

  const insuranceAge = getInsuranceAge();
  const technicalAge = getTechnicalAge();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Détails du véhicule
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* En-tête avec plaque et statut */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold">{vehicle.plate_number}</h2>
                {vehicle.logiconnect_id && (
                  <LogiConnectIdBadge 
                    id={vehicle.logiconnect_id} 
                    showLabel={false}
                    size="sm"
                  />
                )}
              </div>
              <p className="text-muted-foreground">{vehicle.vehicle_type_label}</p>
            </div>
            <Badge className={getStatusColor(vehicle.status)}>
              {vehicle.status_display}
            </Badge>
          </div>

          {/* Informations générales */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {vehicle.logiconnect_id && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">ID LogiConnect</p>
                  <LogiConnectIdBadge 
                    id={vehicle.logiconnect_id} 
                    showLabel={false}
                    size="default"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Marque</p>
                  <p className="font-medium">{vehicle.make}</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground">Modèle</p>
                  <p className="font-medium">{vehicle.model}</p>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground">Année</p>
                  <p className="font-medium">{vehicle.year}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capacités */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5" />
                Capacités
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Package className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Capacité volumique</p>
                    <p className="font-medium">
                      {vehicle.capacity_volume ? `${vehicle.capacity_volume} m³` : 'Non renseignée'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Weight className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Capacité de poids</p>
                    <p className="font-medium">
                      {vehicle.capacity_weight ? `${vehicle.capacity_weight} kg` : 'Non renseignée'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assurance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Assurance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusIcon(vehicle.insurance_status)}
                <span className={`font-medium ${getInsuranceColor(vehicle.insurance_status)}`}>
                  {vehicle.insurance_valid_until 
                    ? format(new Date(vehicle.insurance_valid_until), 'PPP', { locale: fr })
                    : 'Non renseignée'
                  }
                </span>
              </div>
              
              {insuranceAge && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className={`text-sm font-medium ${insuranceAge.color}`}>
                    {insuranceAge.text}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contrôle technique */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Contrôle technique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                {getStatusIcon(vehicle.technical_control_status)}
                <span className={`font-medium ${getTechnicalColor(vehicle.technical_control_status)}`}>
                  {vehicle.technical_control_until 
                    ? format(new Date(vehicle.technical_control_until), 'PPP', { locale: fr })
                    : 'Non renseigné'
                  }
                </span>
              </div>
              
              {technicalAge && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className={`text-sm font-medium ${technicalAge.color}`}>
                    {technicalAge.text}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Informations système */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations système</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">ID</p>
                  <p className="font-mono text-xs">{vehicle.id}</p>
                </div>
                
                <div>
                  <p className="text-muted-foreground">Transporteur</p>
                  <p className="font-medium">{vehicle.transporter_name}</p>
                </div>
                
                <div>
                  <p className="text-muted-foreground">Créé le</p>
                  <p className="font-medium">
                    {vehicle.created_at ? format(new Date(vehicle.created_at), 'PPP à HH:mm', { locale: fr }) : 'N/A'}
                  </p>
                </div>
                
                <div>
                  <p className="text-muted-foreground">Modifié le</p>
                  <p className="font-medium">
                    {vehicle.updated_at ? format(new Date(vehicle.updated_at), 'PPP à HH:mm', { locale: fr }) : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Image du véhicule */}
          {vehicle.image && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Image du véhicule
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative w-full h-64 bg-muted rounded-lg overflow-hidden">
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </CardContent>
            </Card>
          )}


          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => onEdit(vehicle)}
              className="flex-1"
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
            <Button
              variant="outline"
              onClick={() => onDelete(vehicle)}
              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VehicleDetailsDialog;